import { useLazyFetchBookingDetailsQuery } from '../../api/rtkQuery/fetchBookingDetailsApi'
import {
    isFailedBookingStatus,
    isInProgressBookingStatus,
    isSuccessBookingStatus
} from '../../utils/confirmation/bookingStatusUi'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const POLL_INTERVAL_MS = 5000
const MAX_POLLS = 10
const POLL_COUNT_KEY = (id: string) => `confirmationPollCount:${id}`

const readCount = (id: string): number => {
    if (typeof window === 'undefined' || !id) return MAX_POLLS
    const n = Number(window.sessionStorage.getItem(POLL_COUNT_KEY(id)) ?? 0)
    return Number.isFinite(n) && n >= 0 ? Math.min(n, MAX_POLLS) : 0
}

const writeCount = (id: string, n: number) => {
    if (typeof window === 'undefined' || !id) return
    window.sessionStorage.setItem(POLL_COUNT_KEY(id), String(Math.min(Math.max(n, 0), MAX_POLLS)))
}

const isTerminalStatus = (s?: string) => isSuccessBookingStatus(s) || isFailedBookingStatus(s)
const isEmptyPnr = (pnr?: string | null) => !pnr || pnr.trim() === ''

const getStatusFromDetails = (details: any[]): string | undefined => {
    if (!Array.isArray(details) || details.length === 0) return undefined
    if (details.some(d => d?.bookingStatus === 'BOOKING_FAILED')) return 'BOOKING_FAILED'
    if (details.some(d => d?.bookingStatus === 'BOOKING_IN_PROGRESS')) return 'BOOKING_IN_PROGRESS'
    if (details.some(d => d?.bookingStatus === 'TICKET_PENDING')) return 'TICKET_PENDING'
    if (details.some(d => d?.bookingStatus === 'BOOKING_SUCCESS')) return 'BOOKING_SUCCESS'
    return undefined
}

export const hasCompletedPollingCycle = (wasFetching: boolean, isFetching: boolean) =>
    wasFetching && !isFetching

export const getAsyncPollingDerivedState = ({
    isAsyncFlow,
    shouldUseIntervalPolling,
    pollCount,
    polledStatus,
    initialStatus
}: {
    isAsyncFlow: boolean
    shouldUseIntervalPolling: boolean
    pollCount: number
    polledStatus?: string
    initialStatus?: string
}) => {
    const reachedPollCap = isAsyncFlow && pollCount >= MAX_POLLS
    const hasTerminalPolledStatus = isTerminalStatus(polledStatus)
    const shouldForceInProgress = isAsyncFlow && reachedPollCap && !hasTerminalPolledStatus
    const isTimedOut = isAsyncFlow && reachedPollCap && !hasTerminalPolledStatus
    const isPolling = isAsyncFlow && shouldUseIntervalPolling && !hasTerminalPolledStatus
    const currentBookingStatus = isAsyncFlow
        ? isPolling && !polledStatus
            ? 'BOOKING_IN_PROGRESS'
            : shouldForceInProgress
              ? 'BOOKING_IN_PROGRESS'
              : polledStatus || initialStatus || ''
        : initialStatus || ''

    return { reachedPollCap, hasTerminalPolledStatus, shouldForceInProgress, isTimedOut, isPolling, currentBookingStatus }
}

const mergePnrMap = (primaryMap: any, fallbackDetails: any[]) => {
    if (!Array.isArray(fallbackDetails) || fallbackDetails.length === 0) return primaryMap
    const base = primaryMap || {}
    const merged = { ...base }
    fallbackDetails.forEach((item, i) => {
        const key = item?.journeyId != null ? String(item.journeyId) : String(i + 1)
        const existing = base[key] || {}
        merged[key] = {
            ...existing,
            bookingStatusCode: existing.bookingStatusCode || item?.bookingStatus || '',
            airlinePnr: isEmptyPnr(existing.airlinePnr) ? item?.airlinePnr || '' : existing.airlinePnr,
            gdsPnr: existing.gdsPnr || item?.gdsPnr || '',
            ticketNumber: existing.ticketNumber || item?.ticketNumber || null
        }
    })
    return merged
}

const isSameBookingStatusMap = (l: any, r: any) => {
    if (l === r) return true
    if (!l || !r) return false
    const lk = Object.keys(l)
    if (lk.length !== Object.keys(r).length) return false
    return lk.every(
        k =>
            l[k]?.bookingStatusCode === r[k]?.bookingStatusCode &&
            l[k]?.airlinePnr === r[k]?.airlinePnr &&
            l[k]?.gdsPnr === r[k]?.gdsPnr &&
            JSON.stringify(l[k]?.ticketNumber ?? null) === JSON.stringify(r[k]?.ticketNumber ?? null)
    )
}

export const useBookConfirmPollingStatus = (
    id: string,
    initialStatus?: string,
    primaryBookingStatusDetailsMap?: any,
    isAsyncFlow = false
) => {
    const [pollCount, setPollCount] = useState(0)
    const [hasReceivedFirstPoll, setHasReceivedFirstPoll] = useState(!isAsyncFlow)
    const [hasReachedTerminalStatus, setHasReachedTerminalStatus] = useState(false)

    const pollCountRef = useRef(0)
    const isPollingActiveRef = useRef(false)
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const prevIsFetchingRef = useRef(false)
    const lastTriggeredIdRef = useRef<string | null>(null)
    const latestDataRef = useRef<any[]>([])
    const stableMergedMapRef = useRef<any>(primaryBookingStatusDetailsMap)

    const [triggerFetch, { data = [], isFetching }] = useLazyFetchBookingDetailsQuery()

    latestDataRef.current = data

    const clearTimer = useCallback(() => {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current)
            timerRef.current = null
        }
    }, [])

    const isPollAllowed = useCallback((): boolean => {
        if (!id || !isPollingActiveRef.current) return false
        return pollCountRef.current < MAX_POLLS && readCount(id) < MAX_POLLS
    }, [id])

    const dispatchFetch = useCallback(() => {
        if (!isPollAllowed()) return
        triggerFetch({ bookingId: id })
    }, [id, isPollAllowed, triggerFetch])

    const scheduleNextPoll = useCallback(() => {
        clearTimer()
        if (!isPollAllowed()) return
        timerRef.current = setTimeout(() => {
            timerRef.current = null
            dispatchFetch()
        }, POLL_INTERVAL_MS)
    }, [clearTimer, isPollAllowed, dispatchFetch])

    useEffect(() => {
        isPollingActiveRef.current = false
        clearTimer()
        prevIsFetchingRef.current = false

        if (!isAsyncFlow || !id) {
            pollCountRef.current = 0
            setPollCount(0)
            setHasReceivedFirstPoll(!isAsyncFlow)
            setHasReachedTerminalStatus(false)
            lastTriggeredIdRef.current = null
            return
        }

        const stored = readCount(id)
        pollCountRef.current = stored
        setPollCount(stored)
        setHasReceivedFirstPoll(false)
        setHasReachedTerminalStatus(false)

        if (lastTriggeredIdRef.current !== id) {
            lastTriggeredIdRef.current = id
            if (stored < MAX_POLLS) {
                isPollingActiveRef.current = true
                triggerFetch({ bookingId: id })
            } else {
                isPollingActiveRef.current = false
                triggerFetch({ bookingId: id })
            }
        } else {
            // Re-mount for the same ID (e.g. Strict Mode). Restore active state but skip redundant fetch.
            if (stored < MAX_POLLS) {
                isPollingActiveRef.current = true
            } else {
                isPollingActiveRef.current = false
            }
        }

        return () => {
            isPollingActiveRef.current = false
            clearTimer()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isAsyncFlow, clearTimer])

    useEffect(() => {
        const wasFetching = prevIsFetchingRef.current
        prevIsFetchingRef.current = isFetching

        if (!wasFetching || isFetching) return
        if (!isAsyncFlow || !id) return

        setHasReceivedFirstPoll(true)

        if (!isPollingActiveRef.current) {
            return
        }

        const next = Math.min(pollCountRef.current + 1, MAX_POLLS)
        writeCount(id, next)
        pollCountRef.current = next
        setPollCount(next)

        const polledStatus = getStatusFromDetails(latestDataRef.current)
        if (isTerminalStatus(polledStatus)) {
            setHasReachedTerminalStatus(true)
            isPollingActiveRef.current = false
            clearTimer()
            return
        }

        if (next < MAX_POLLS) {
            scheduleNextPoll()
        } else {
            isPollingActiveRef.current = false
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFetching])

    const polledStatus = getStatusFromDetails(latestDataRef.current)

    useEffect(() => {
        if (!isAsyncFlow || !polledStatus) return
        if (isTerminalStatus(polledStatus) && !hasReachedTerminalStatus) {
            setHasReachedTerminalStatus(true)
            isPollingActiveRef.current = false
            clearTimer()
        }
    }, [isAsyncFlow, polledStatus, hasReachedTerminalStatus, clearTimer])

    const shouldUseIntervalPolling = isPollingActiveRef.current && pollCount < MAX_POLLS && !hasReachedTerminalStatus

    const { isTimedOut, isPolling, currentBookingStatus } = getAsyncPollingDerivedState({
        isAsyncFlow,
        shouldUseIntervalPolling,
        pollCount,
        polledStatus,
        initialStatus
    })

    const isPnrLoading = isAsyncFlow && isInProgressBookingStatus(currentBookingStatus) && !isTimedOut

    const mergedBookingStatusDetailsMap = useMemo(() => {
        if (!isAsyncFlow) return primaryBookingStatusDetailsMap
        const nextMap = mergePnrMap(primaryBookingStatusDetailsMap, latestDataRef.current)
        if (isSameBookingStatusMap(stableMergedMapRef.current, nextMap)) return stableMergedMapRef.current
        stableMergedMapRef.current = nextMap
        return nextMap
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAsyncFlow, primaryBookingStatusDetailsMap, data])

    return {
        currentBookingStatus,
        isTimedOut,
        isPolling: isAsyncFlow ? isPolling : false,
        isPnrLoading,
        mergedBookingStatusDetailsMap,
        hasReceivedFirstPoll
    }
}
